/**
 * Existence checks shared by the reducer and the outcome applier. "Never
 * trust" (§14) means every id that arrives from outside the sim — a model
 * emission, a UI action — is looked up before it is used.
 */

import { findEvent } from '../engine/memory/events.js';
import type { Actor, EntityRef, EventId, LocationId, NPC, NpcId, StanceSubject, WorldState } from './types/index.js';

export function isFiniteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

/** An NPC with a card who is not dead. Jailed and gone still have opinions. */
export function livingNpc(state: WorldState, id: unknown): NPC | null {
  if (typeof id !== 'string') return null;
  const npc = state.npcs[id as NpcId];
  return npc && npc.status !== 'dead' ? npc : null;
}

export function actorExists(state: WorldState, actor: unknown): actor is Actor {
  return actor === 'player' || livingNpc(state, actor) !== null;
}

export function refExists(state: WorldState, ref: unknown): ref is EntityRef {
  if (typeof ref !== 'object' || ref === null) return false;
  const r = ref as { kind?: unknown; id?: unknown };
  switch (r.kind) {
    case 'player':
      return true;
    case 'npc':
      return typeof r.id === 'string' && (r.id as NpcId) in state.npcs;
    case 'location':
      return typeof r.id === 'string' && (r.id as LocationId) in state.locations;
    case 'faction':
      return typeof r.id === 'string' && r.id in state.factions;
    case 'secret':
      return typeof r.id === 'string' && r.id in state.secrets;
    case 'event':
      return typeof r.id === 'string' && findEvent(state, r.id as EventId) !== undefined;
    case 'situation':
      return typeof r.id === 'string' && r.id in state.situations;
    default:
      return false;
  }
}

export function subjectExists(state: WorldState, subject: unknown): subject is StanceSubject {
  if (typeof subject !== 'object' || subject === null) return false;
  const s = subject as { kind?: unknown };
  if (s.kind === 'player' || s.kind === 'npc' || s.kind === 'faction' || s.kind === 'situation') {
    return refExists(state, subject);
  }
  return false;
}
